import { FC, useEffect, useState } from 'react'
import { parseIds, useSearchParams } from '@/hooks/useSearchParams'
import usePagination from '@/hooks/usePagination'
import Pagination from '@UI/New/Pagination'
import { reportAPI } from '@/api'
import CustomTable from '@common/CustomTable/Table'
import { IGroupAllLessonItem } from '../../utils/types'
import useGroupAllLessonsColumns from '../TableColumns/useGroupAllLessonsColumns'
import cn from '../GroupLessonReportTable/index.module.css'
import ChangeLessonReportStatusModal from '../MakeLessonReportModal/ChangeLessonReportStatusModal'
import EditEntityModal from '@modals/EditEntityModal/EditEntityModal'

interface IGroupAllLessonsTable {
  studentId?: number | string | null
  directionId?: string | null
  startDate?: string | null
  endDate?: string | null
  status?: string | null
}

const GroupAllLessonsTable: FC<IGroupAllLessonsTable> = ({
  studentId,
  directionId: selectedDirectionId,
  startDate,
  endDate,
  status,
}) => {
  const [startDateFromSearch] = useSearchParams('all-start-date')
  const [endDateFromSearch] = useSearchParams('all-end-date')
  const [legacyClassId] = useSearchParams('all-class-id')
  const [classIds] = useSearchParams('classIds')
  const [classFolderIds] = useSearchParams('classFolderIds')
  const [directionId] = useSearchParams('direction-id')
  const [seminarianId] = useSearchParams('all-seminarian-id')
  const [statusFromSearch] = useSearchParams('all-status')
  const [search] = useSearchParams('all-search')
  const [franchiseIds] = useSearchParams('franchise-ids')
  const [editReport, setEditReport] = useState<IGroupAllLessonItem | null>(null)
  const [userModal, setUserModal] = useState<IUser | null>(null)
  const [sorting, setSorting] = useState(
    [
      'date-time',
      'seminarian',
      'course',
      'group',
      'students-count',
      'duration',
      'telebos-join-time',
      'telebos-late-minutes',
      'telebos-duration',
      'status',
      'comment',
      'salary',
    ].map((key) => ({ key, order: 'none' as 'asc' | 'desc' | 'none' })),
  )

  const currentSort = sorting.find((item) => item.order !== 'none')
  const effectiveStartDate = startDate ?? startDateFromSearch
  const effectiveEndDate = endDate ?? endDateFromSearch
  const effectiveStatus = status ?? statusFromSearch
  const effectiveDirectionId = selectedDirectionId === undefined ? directionId : selectedDirectionId

  const { state, loading, getState, ...params } = usePagination({
    api: reportAPI.group.allLessons,
    params: {
      start_date: effectiveStartDate,
      end_date: effectiveEndDate,
      ...(classIds && classIds !== 'all'
        ? { class_ids: JSON.stringify(parseIds(classIds)) }
        : {}),
      ...(classFolderIds && classFolderIds !== 'all'
        ? { class_folder_ids: JSON.stringify(parseIds(classFolderIds)) }
        : {}),
      ...(legacyClassId && !classIds && !classFolderIds
        ? { class_id: legacyClassId }
        : {}),
      ...(effectiveDirectionId ? { direction_id: effectiveDirectionId } : {}),
      ...(seminarianId ? { seminarian_id: seminarianId } : {}),
      ...(studentId ? { student_id: studentId } : {}),
      ...(effectiveStatus && effectiveStatus !== 'all'
        ? { status: effectiveStatus }
        : {}),
      ...(search ? { search } : {}),
      ...(franchiseIds ? { franchise_ids: franchiseIds } : {}),
      ...(currentSort
        ? { sort_key: currentSort.key, sort_order: currentSort.order }
        : {}),
    },
    firstRequest: false,
    initialPerPage: 100,
  })

  useEffect(() => {
    getState()
    params.setCurrentPage(1)
  }, [
    effectiveStartDate,
    effectiveEndDate,
    legacyClassId,
    classIds,
    classFolderIds,
    effectiveDirectionId,
    seminarianId,
    studentId,
    effectiveStatus,
    search,
    franchiseIds,
    currentSort?.key,
    currentSort?.order,
  ])

  const columns = useGroupAllLessonsColumns({
    onEdit: (report) => setEditReport(report),
    onSeminarianClick: (seminarian) => {
      if (!seminarian?.id) return
      setUserModal(seminarian)
    },
  })

  return (
    <div className={cn.content}>
      <CustomTable<IGroupAllLessonItem>
        data={state as IGroupAllLessonItem[]}
        columns={columns}
        loading={loading}
        sorting={sorting}
        onSortingClick={(key) => {
          setSorting((prev) =>
            prev.map((item) => ({
              ...item,
              order:
                item.key === key
                  ? item.order === 'none'
                    ? 'asc'
                    : item.order === 'asc'
                    ? 'desc'
                    : 'none'
                  : 'none',
            })),
          )
        }}
      />
      <Pagination {...params} />
      <ChangeLessonReportStatusModal
        open={!!editReport}
        report={editReport as any}
        onSuccess={() => {
          getState()
          setEditReport(null)
        }}
        onDismiss={() => setEditReport(null)}
      />
      <EditEntityModal
        type="employee"
        open={!!userModal}
        employeeId={userModal?.id ?? null}
        onDismiss={() => setUserModal(null)}
        onUpdate={() => setUserModal(null)}
      />
    </div>
  )
}

export default GroupAllLessonsTable
